import { useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import axios, { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { groupByOrg, useAvailableContexts } from '@/hooks/useContexts';
import { config } from '@/config';
import { StepUpChallenge } from '@/features/auth/StepUpChallenge';
import { isStepUpRequired, useStepUp } from '@/features/auth/stepUp';
import { ContinueAsAnotherAccount } from '@/components/auth/ContinueAsAnotherAccount';

/** The two seeded MCP preset roles (mirror `preset_role_name` in the backend). */
type Preset = 'read_plan' | 'read_plan_execute';

const PRESETS: { value: Preset; title: string; blurb: string }[] = [
  {
    value: 'read_plan',
    title: 'Read & Plan',
    blurb: 'Read everything in this context and draft change-management plans. Cannot execute changes.',
  },
  {
    value: 'read_plan_execute',
    title: 'Read, Plan & Execute',
    blurb: 'Everything above, plus execute the change-management plans it creates.',
  },
];

interface ApproveResponse {
  redirect_url: string;
}
// The OAuth AS returns RFC 6749 errors: { error, error_description }.
interface ApiError {
  error?: string;
  error_description?: string;
}

/** Turn an OAuth error response into something a human can act on. */
function friendlyOAuthError(data: ApiError | undefined): string {
  switch (data?.error) {
    case 'invalid_client':
      return "This application isn't registered, so it can't be authorized. Start the connection again from the app.";
    case 'access_denied':
      return 'Authorization was denied.';
    case 'server_error':
      return 'Something went wrong on our end. Please try again.';
    case 'invalid_grant':
      return 'This authorization request has expired. Start the connection again from the app.';
    case 'invalid_request':
      // These carry a specific, human-readable reason (e.g. context/ceiling).
      return data.error_description ?? 'The authorization request was invalid.';
    default:
      return data?.error_description ?? 'Authorization failed. Please try again.';
  }
}

/**
 * OAuth consent screen (`/oauth/consent`). The backend's `GET /oauth/authorize`
 * validates the client + redirect_uri and bounces the browser here with the
 * original query string. The human then picks the *context* the agent will act
 * in and a *permission preset*, and we POST `/api/oauth/authorize` to bind a
 * service-principal MCP account (enforcing the delegation ceiling) and mint the
 * authorization code — then follow `redirect_url` back to the client.
 *
 * Public route so we can preserve the OAuth params across login: an
 * unauthenticated visitor is sent to `/signin?returnTo=…` (see McpServer.md).
 */
export default function OAuthConsent() {
  const [params] = useSearchParams();
  const { user, isLoading: authLoading } = useAuth();

  // The OAuth request the client handed us. The backend re-validates all of
  // these; we just forward them plus the human's context + preset choice.
  const responseType = params.get('response_type');
  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';
  const codeChallenge = params.get('code_challenge') ?? '';
  const codeChallengeMethod = params.get('code_challenge_method') ?? 'S256';
  const state = params.get('state');
  const resource = params.get('resource') ?? '';
  const scope = params.get('scope');

  // Deliberately the full cross-organization list. An MCP grant is scoped to one
  // context, and `create_mcp_account` accepts any organization the human can
  // reach — it checks `has_org_access` against the *chosen* context's
  // organization, then a delegation ceiling in that context. Authorizing from an
  // external process is exactly when the context you want may not be the one you
  // happen to be viewing, so filtering to the current organization would remove
  // valid answers. The record-creating pickers elsewhere are the opposite case:
  // there a foreign context is rejected on submit, so offering it is a dead end.
  const contexts = useAvailableContexts(user?.organizationId);

  // Which is exactly why every row has to name its organization. Two
  // organizations each with an "Org Admin" are indistinguishable by context name,
  // and an ambiguous pick here does not fail loudly — it succeeds, granting a
  // durable credential (possibly execute) somewhere the user did not intend.
  const orgNameById = useMemo(
    () => new Map((user?.organizations ?? []).map((o) => [o.id, o.name])),
    [user?.organizations],
  );
  const contextGroups = useMemo(
    () => groupByOrg(contexts.data ?? [], orgNameById),
    [contexts.data, orgNameById],
  );
  // With one organization the heading is the same word on every row; the
  // per-row qualifier still distinguishes the divisions within it.
  const showOrgName = contextGroups.length > 1;
  const [contextId, setContextId] = useState<string>('');
  const [preset, setPreset] = useState<Preset>('read_plan');
  const [accountName, setAccountName] = useState<string>('');
  // An MCP grant is durable — it survives sign-out and per-device revocation — so the consent
  // click is exactly the kind of thing §4.6 asks a live cookie to back up with a fresh proof.
  const { challenge: stepUp, satisfy, clear } = useStepUp();

  // Default the context to the one the user is currently acting in, else the
  // first available. Only sets it once (until the user picks another).
  const defaultContextId = user?.activeContext?.id ?? contexts.data?.[0]?.id;
  if (!contextId && defaultContextId) setContextId(defaultContextId);

  const missingParams = useMemo(
    () => responseType !== 'code' || !clientId || !redirectUri || !codeChallenge || !resource,
    [responseType, clientId, redirectUri, codeChallenge, resource],
  );

  const approve = useMutation<ApproveResponse, AxiosError<ApiError>>({
    mutationFn: async () => {
      const res = await axios.post<ApproveResponse>(
        `${config.backendApi}/api/oauth/authorize`,
        {
          client_id: clientId,
          redirect_uri: redirectUri,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod,
          state,
          resource,
          scope,
          context_id: contextId,
          preset,
          account_name: accountName.trim() || undefined,
        },
        { withCredentials: true },
      );
      return res.data;
    },
    // Follow the redirect back to the client (may be a custom scheme or
    // localhost) — a full-page navigation, not a router push.
    onSuccess: (data) => { window.location.assign(data.redirect_url); },
    onError: (err) => {
      // ⚠ **A step-up refusal is an instruction, not a failure.** The server answers 403 with
      // the rung it wants — a key when the account holds one usable here, a password when it
      // does not. Reporting it as a toast, which is what this did, left an enrolled user stopped
      // with nothing to click: the stronger the account, the more thoroughly it was stuck.
      const c = isStepUpRequired(err);
      if (c) { satisfy(c); return; }
      toast.error(friendlyOAuthError(err.response?.data));
    },
  });

  // Deny: bounce back to the client with the standard OAuth error so it can
  // fail gracefully instead of hanging.
  const deny = () => {
    try {
      const url = new URL(redirectUri);
      url.searchParams.set('error', 'access_denied');
      if (state) url.searchParams.set('state', state);
      window.location.assign(url.toString());
    } catch {
      toast.error('Cannot return to the application — the redirect URL is invalid.');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
      </div>
    );
  }

  // Preserve the full OAuth request (path + query) through login.
  if (!user) {
    const returnTo = `/oauth/consent?${params.toString()}`;
    return <Navigate to={`/signin?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  const shell = (children: React.ReactNode) => (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="bg-card border border-border rounded-lg p-8 max-w-lg w-full space-y-6">{children}</div>
    </div>
  );

  if (missingParams) {
    return shell(
      <div className="text-center space-y-3">
        <XCircle className="h-10 w-10 text-destructive mx-auto" />
        <h1 className="text-lg font-semibold">Invalid authorization request</h1>
        <p className="text-sm text-muted-foreground">
          This link is missing required parameters. Start the connection again from the application.
        </p>
      </div>,
    );
  }

  if (!user.organizationId) {
    return shell(
      <div className="text-center space-y-3">
        <XCircle className="h-10 w-10 text-destructive mx-auto" />
        <h1 className="text-lg font-semibold">No organization</h1>
        <p className="text-sm text-muted-foreground">
          You need to belong to an organization before you can authorize an application.
        </p>
        <ContinueAsAnotherAccount />
      </div>,
    );
  }

  return shell(
    <>
      <div className="flex items-start gap-3">
        <ShieldCheck className="h-8 w-8 shrink-0 text-primary" />
        <div>
          <h1 className="text-lg font-semibold">Authorize application</h1>
          <p className="text-sm text-muted-foreground">
            An application wants to access your network on your behalf as a dedicated agent account. Choose what it can
            do and where.
          </p>
        </div>
      </div>

      <dl className="rounded-md border border-border-subtle bg-accent/10 px-3 py-2 text-xs text-muted-foreground space-y-1">
        <div className="flex justify-between gap-2">
          <dt>Account</dt>
          <dd className="truncate">{user.email}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>Application</dt>
          <dd className="font-mono truncate">{clientId}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>Resource</dt>
          <dd className="font-mono truncate">{resource}</dd>
        </div>
      </dl>

      <div className="space-y-1.5">
        <Label htmlFor="consent-context">Context</Label>
        <Select value={contextId} onValueChange={setContextId} disabled={contexts.isLoading}>
          <SelectTrigger id="consent-context">
            <SelectValue placeholder={contexts.isLoading ? 'Loading…' : 'Select a context'} />
          </SelectTrigger>
          <SelectContent>
            {contextGroups.map((g) => (
              <SelectGroup key={g.orgId}>
                <SelectLabel>{g.orgName}</SelectLabel>
                {g.contexts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {/* The qualifier rides on the item rather than the heading so
                        it survives into the closed trigger, which is the state the
                        user actually approves from. */}
                    <span className="flex items-center gap-2">
                      <span className="truncate">{c.name}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {[
                          c.divisionId ? c.divisionName ?? 'Division' : 'org',
                          showOrgName ? g.orgName : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          The agent acts only within this context, and never with more than your own permissions in it.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Permissions</Label>
        <RadioGroup value={preset} onValueChange={(v) => { setPreset(v as Preset); }} className="gap-2">
          {PRESETS.map((p) => (
            <label
              key={p.value}
              htmlFor={`preset-${p.value}`}
              className="flex cursor-pointer items-start gap-3 rounded-md border border-border-subtle p-3 hover:bg-accent/20 has-[:checked]:border-primary has-[:checked]:bg-accent/20"
            >
              <RadioGroupItem value={p.value} id={`preset-${p.value}`} className="mt-0.5" />
              <div className="space-y-0.5">
                <div className="text-sm font-medium">{p.title}</div>
                <div className="text-xs text-muted-foreground">{p.blurb}</div>
              </div>
            </label>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="consent-name">Account name (optional)</Label>
        <Input
          id="consent-name"
          value={accountName}
          onChange={(e) => { setAccountName(e.target.value); }}
          placeholder="e.g. Claude — network assistant"
        />
        <p className="text-xs text-muted-foreground">Shown in audit logs so you can tell this agent apart.</p>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button variant="outline" onClick={deny} disabled={approve.isPending}>
          Deny
        </Button>
        <Button onClick={() => { approve.mutate(); }} disabled={!contextId || approve.isPending} className="gap-1.5">
          {approve.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Authorize
        </Button>
      </div>

      {/* ⚠ **Retried once the proof lands, not abandoned.** The consent choices — context,
          preset, account name — are still on screen and still in state, so re-submitting is the
          whole of what the user meant by clicking Authorize. Making them re-pick after proving
          who they are would be a second penalty for having a key. */}
      {stepUp && (
        <StepUpChallenge
          challenge={stepUp}
          onSatisfied={() => { clear(); approve.mutate(); }}
          onCancel={clear}
        />
      )}
      <ContinueAsAnotherAccount />
    </>,
  );
}
